import { WF_STATIC_BASE } from './wf-static';

declare global {
  interface Window {
    Webflow?: {
      destroy?: () => void;
      ready?: () => void;
      require?: ( name: string ) => { init?: () => void };
    };
  }
}

/**
 * Collection templates use either a .main-wrapper block or a bare .section-page-header (technology export).
 * Strips duplicated chrome: navbar/footer in the HTML file are omitted; Angular supplies wf-navbar / wf-footer.
 */
export function extractWfMainFragment( html: string ): string | null {

  const footerIdx = html.indexOf( '<footer class="footer-component">' );

  if ( footerIdx < 0 ) {

    return null;
  }

  const divMainIdx = html.indexOf( '<div class="main-wrapper">' );
  const mainMainIdx = html.indexOf( '<main class="main-wrapper">' );
  const mainWrapperCandidates = [ divMainIdx, mainMainIdx ].filter( i => i >= 0 );
  const mainWrapperIdx = mainWrapperCandidates.length ? Math.min( ...mainWrapperCandidates ) : -1;
  const pageHeaderIdx = html.indexOf( '<header class="section-page-header">' );

  let start: number;

  if ( mainWrapperIdx >= 0 && ( pageHeaderIdx < 0 || mainWrapperIdx < pageHeaderIdx ) ) {

    start = mainWrapperIdx;
  } else if ( pageHeaderIdx >= 0 ) {

    start = pageHeaderIdx;
  } else {

    return null;
  }

  if ( start >= footerIdx ) {

    return null;
  }

  return html.slice( start, footerIdx );
}

/** data-w-id reutilizado en cabeceras de página (legal, soluciones, tecnología, pricing, etc.). */
const IX2_PAGE_HEADER_LOCK_IDS = [
  '82ce19ce-e073-c7f3-fa73-c3741f486335',
  '912747f5-746f-c686-78cd-1570da414d89',
  '912747f5-746f-c686-78cd-1570da414d92'
] as const;

/** Quita `style="…"` del `<div …>` que contiene un `data-w-id` dado (cualquier orden de atributos). */
function stripStyleAttributeFromDivOpenTagWithDataWId( html: string, dataWId: string ): string {

  const needle = `data-w-id="${ dataWId }"`;
  let i = 0;
  let result = '';

  while ( i < html.length ) {

    const idPos = html.indexOf( needle, i );

    if ( idPos < 0 ) {

      result += html.slice( i );
      break;
    }

    const divOpen = html.lastIndexOf( '<div', idPos );

    if ( divOpen < i || divOpen < 0 ) {

      result += html.slice( i, idPos + needle.length );
      i = idPos + needle.length;
      continue;
    }

    const gt = html.indexOf( '>', idPos );

    if ( gt < 0 ) {

      result += html.slice( i );
      break;
    }

    const tag = html.slice( divOpen, gt + 1 );

    if ( !/^<div\b/i.test( tag ) || tag.indexOf( needle ) < 0 ) {

      i = idPos + 1;
      continue;
    }

    const cleaned = tag.replace( /\sstyle="[^"]*"/gi, '' );

    result += html.slice( i, divOpen ) + cleaned;
    i = gt + 1;
  }

  return result;
}

/**
 * Webflow export: cabeceras con IX2 pueden llevar `style="opacity:0; transform:..."` en el HTML.
 * El orden de atributos en el export no es fijo; se normaliza antes de `innerHTML`.
 */
function stripIx2HiddenHeaderStylesFromFragment( fragment: string ): string {

  let out = fragment;

  for ( const id of IX2_PAGE_HEADER_LOCK_IDS ) {

    out = stripStyleAttributeFromDivOpenTagWithDataWId( out, id );
  }

  return out;
}

/**
 * Retardo por defecto para revelar cabeceras IX2 por tiempo en páginas con HTML inyectado,
 * en lugar de depender del evento scroll del usuario.
 */
export const WF_IX2_HEADER_REVEAL_DELAY_MS = 500;

const WF_IX2_SCROLL_NUDGE_PX = 1;

/**
 * Desplaza el viewport 1px y lo restaura en el siguiente frame para disparar scroll / IntersectionObserver
 * de IX2 cuando la página ya está arriba del todo (p. ej. legal inyectado en SPA).
 */
export function nudgeViewportScrollToWakeIx2(): void {

  const docEl = document.documentElement;
  const prev =
    window.scrollY ??
    docEl.scrollTop ??
    document.body.scrollTop ??
    0;

  window.scrollTo( 0, prev + WF_IX2_SCROLL_NUDGE_PX );

  requestAnimationFrame( () => {

    window.scrollTo( 0, prev );
  } );
}

/** Segundo micro-scroll tras unlock retardado (cabeceras HTML inyectado). */
const WF_IX2_SCROLL_NUDGE_AFTER_UNLOCK_MS = 32;

/** Segundo micro-scroll cuando el rebind global no usa retardo (p. ej. Home). */
const WF_IX2_SCROLL_NUDGE_AFTER_IMMEDIATE_REBIND_MS = 48;

export interface ScheduleIx2ScrollNudgesOptions {

  /** Si hay retardo de cabecera, el segundo nudge va tras `delay + WF_IX2_SCROLL_NUDGE_AFTER_UNLOCK_MS`. */
  headerRevealDelayMs?: number;
}

/**
 * Dos micro-scrolls: uno en el siguiente frame y otro tras un breve retardo (alineado con shell legal).
 */
export function scheduleIx2ScrollNudgesAfterRebind( options?: ScheduleIx2ScrollNudgesOptions ): void {

  const delayMs = options?.headerRevealDelayMs;
  const followMs = delayMs != null && delayMs > 0
    ? delayMs + WF_IX2_SCROLL_NUDGE_AFTER_UNLOCK_MS
    : WF_IX2_SCROLL_NUDGE_AFTER_IMMEDIATE_REBIND_MS;

  requestAnimationFrame( () => {

    nudgeViewportScrollToWakeIx2();
  } );

  window.setTimeout( () => {

    nudgeViewportScrollToWakeIx2();
  }, followMs );
}

export interface RebindWebflowIx2Options {

  /** Si es mayor que 0, la limpieza de bloqueos de cabecera se programa tras este retardo (ms). */
  headerRevealDelayMs?: number;
}

function runIx2HeaderUnlockPasses(): void {

  window.dispatchEvent( new Event( 'scroll', { bubbles: true } ) );
  clearIx2HeaderLocksInSubtree( document.body );

  requestAnimationFrame( () => {

    requestAnimationFrame( () => {

      clearIx2HeaderLocksInSubtree( document.body );
    } );
  } );
}

function scheduleIx2HeaderUnlockPasses( delayMs?: number ): void {

  if ( delayMs != null && delayMs > 0 ) {

    window.setTimeout( () => runIx2HeaderUnlockPasses(), delayMs );

    return;
  }

  runIx2HeaderUnlockPasses();
}

/**
 * Tras `innerHTML` con HTML de Webflow: `NavigationEnd` suele ejecutar `ix2.init` antes de que el HTTP
 * haya insertado el fragmento (a diferencia de flows en carga estática donde el DOM ya existe).
 * Repite destroy → ready → ix2.init cuando el cuerpo inyectado ya está en el documento.
 */
export function rebindWebflowIx2AfterInjectedHtml( options?: RebindWebflowIx2Options ): void {

  const wf = window.Webflow;
  const delayMs = options?.headerRevealDelayMs;

  if ( !wf?.require ) {

    scheduleIx2HeaderUnlockPasses( delayMs );

    return;
  }

  try {

    wf.destroy?.();
    wf.ready?.();
    wf.require( 'ix2' ).init?.();
  } catch {
    /* Webflow bundle not ready */
  }

  scheduleIx2HeaderUnlockPasses( delayMs );
}

/** Quita bloqueos de visibilidad que IX2 deja en cabeceras tras init o al inyectar HTML. */
export function clearIx2HeaderLocksInSubtree( root: Element | Document | null ): void {

  if ( !root ) {

    return;
  }

  for ( const id of IX2_PAGE_HEADER_LOCK_IDS ) {

    root.querySelectorAll( `[data-w-id="${ id }"]` ).forEach( ( node ) => {

      if ( node instanceof HTMLElement ) {

        node.style.removeProperty( 'opacity' );
        node.style.removeProperty( 'transform' );
        node.style.removeProperty( '-webkit-transform' );
        node.style.removeProperty( '-moz-transform' );
        node.style.removeProperty( '-ms-transform' );
      }
    } );
  }
}

/** Relative export paths → deployed /webflow-static URLs; align signup and legal routes with Angular. */
export function rewriteWfStaticPaths( fragment: string ): string {

  const imgBase = `${ WF_STATIC_BASE }/images/`;
  const localizedSignupHref = resolveLocalizedSignupHref();

  const rewritten = fragment
    .replace( /\.\.\/images\//g, imgBase )
    .replace( /"images\//g, `"${ WF_STATIC_BASE }/images/` )
    .replace( /,\s*images\//g, `, ${ WF_STATIC_BASE }/images/` )
    .replace( /href="\/sign-up"/g, `href="${ localizedSignupHref }"` )
    .replace( /href="\/signup"/g, `href="${ localizedSignupHref }"` )
    .replace( /href="\/terms-and-condition"/g, 'href="/terms-of-service"' )
    .replace( /href="\/legal\/privacy"/g, 'href="/privacy-policy"' )
    .replace( /href="\/legal\/terms"/g, 'href="/terms-of-service"' );

  return stripIx2HiddenHeaderStylesFromFragment( rewritten );
}

function resolveLocalizedSignupHref(): string {

  if ( typeof window === 'undefined' || !window.location ) {

    return '/signup';
  }

  const firstSegment = ( window.location.pathname || '' )
    .split( '/' )
    .filter( ( segment ) => segment.length > 0 )[ 0 ]
    ?.toLowerCase();

  if ( firstSegment === 'es' || firstSegment === 'pt' || firstSegment === 'ko' ) {

    return `/${ firstSegment }/signup`;
  }

  return '/signup';
}
